import {
  BackendService,
  BackendServiceOption,
  ColumnFilters,
  CurrentFilter,
  CurrentPagination,
  CurrentSorter,
  FilterChangedArgs,
  MultiColumnSort,
  Pagination,
  PaginationChangedArgs,
  SingleColumnSort,
} from 'angular-slickgrid';

/**
 * Custom Backend Service for SlickGrid
 * Implements server-side filtering, sorting, and pagination
 * Builds query object that will be used to construct custom querystring parameters
 */
export class CustomBackendService implements BackendService {
  private _currentFilters: ColumnFilters | CurrentFilter[] = [];
  private _currentSorters: CurrentSorter[] = [];
  private _currentPagination: CurrentPagination | null = null;
  private _forcedFilters: Record<string, any> = {};
  private pagination?: Pagination;
  private grid?: any;

  options?: BackendServiceOption;

  /**
   * Build and return the backend service query as a JSON string
   * This will be passed to the process() callback
   */
  buildQuery(): string {
    const queryObject = {
      columnFilters: {
        ...this.extractFiltersAsObject(),
        ...this._forcedFilters,
      },
      sortColumns: this._currentSorters,
      pagination: this._currentPagination,
    };

    return JSON.stringify(queryObject);
  }

  /**
   * Initialize the backend service
   * Store reference to pagination object for updating after responses
   */
  init(serviceOptions?: BackendServiceOption, pagination?: Pagination, grid?: any): void {
    this.options = serviceOptions;
    this.pagination = pagination;
    this.grid = grid;

    if (pagination) {
      this._currentPagination = {
        pageNumber: pagination.pageNumber || 1,
        pageSize: pagination.pageSize || 20,
      };
    }
  }

  getCurrentFilters(): ColumnFilters | CurrentFilter[] {
    return this._currentFilters;
  }

  getCurrentPagination(): CurrentPagination | null {
    return this._currentPagination;
  }

  getCurrentSorters(): CurrentSorter[] {
    return this._currentSorters;
  }

  /**
   * Returns the active filters that can be used to constrain a foreign-key
   * options request. The current column is omitted so its selected values do
   * not prevent users from changing that selection.
   */
  getFilterContext(excludeColumnId?: string): Record<string, any> {
    const excludedFilterField = excludeColumnId
      ? this.getFilterField(excludeColumnId)
      : undefined;
    const activeFilters = this.extractFiltersAsObject(excludeColumnId);
    const forcedFilters = { ...this._forcedFilters };

    if (excludeColumnId) {
      delete forcedFilters[excludeColumnId];
    }
    if (excludedFilterField) {
      delete forcedFilters[excludedFilterField];
    }

    return {
      ...activeFilters,
      ...forcedFilters,
    };
  }

  resetPaginationOptions(): void {
    if (this.pagination) {
      this._currentPagination = {
        pageNumber: 1,
        pageSize: this._currentPagination?.pageSize || this.pagination.pageSize || 20,
      };
    }
  }

  updateOptions(serviceOptions?: Partial<BackendServiceOption>): void {
    this.options = { ...this.options, ...serviceOptions };
  }

  updateFilters(columnFilters: ColumnFilters | CurrentFilter[]): void {
    this._currentFilters = columnFilters;
  }

  updateSorters(sortColumns?: CurrentSorter[]): void {
    this._currentSorters = sortColumns || [];
  }

  updatePagination(newPage: number, pageSize: number): void {
    this._currentPagination = {
      pageNumber: newPage,
      pageSize: pageSize,
    };
  }

  clearFilters(): void {
    this._currentFilters = [];
  }

  setForcedFilters(filters: Record<string, any>): void {
    this._forcedFilters = filters || {};
  }

  clearSorters(): void {
    this._currentSorters = [];
  }

  processOnFilterChanged(
    event: Event | KeyboardEvent | undefined,
    args: FilterChangedArgs
  ): string {
    const columnFilters: ColumnFilters = {};

    if (args.columnFilters) {
      Object.keys(args.columnFilters).forEach(key => {
        columnFilters[key] = args.columnFilters[key];
      });
    }

    this._currentFilters = columnFilters;
    return this.buildQuery();
  }

  processOnSortChanged(
    event: Event | undefined,
    args: SingleColumnSort | MultiColumnSort
  ): string {
    const sortColumns = args as MultiColumnSort;

    if (sortColumns && sortColumns.sortCols) {
      this._currentSorters = sortColumns.sortCols.map(col => ({
        columnId: col.sortCol?.params?.queryFieldSorter || col.sortCol.field || '',
        direction: col.sortAsc ? 'ASC' : 'DESC',
      }));
    }

    return this.buildQuery();
  }

  processOnPaginationChanged(
    event: Event | undefined,
    args: PaginationChangedArgs
  ): string {
    this._currentPagination = {
      pageNumber: args.newPage,
      pageSize: args.pageSize,
    };

    return this.buildQuery();
  }

  private extractFiltersAsObject(excludeColumnId?: string): Record<string, any> {
    const filters: Record<string, any> = {};

    if (Array.isArray(this._currentFilters)) {
      this._currentFilters.forEach((filterValue: any) => {
        if (filterValue?.columnId) {
          if (filterValue.columnId === excludeColumnId) {
            return;
          }
          const filterField = filterValue.queryKey || this.getFilterField(filterValue.columnId);
          const processed = this.processFilterValue(filterValue);
          filters[filterField] = processed;
        }
      });
    } else {
      Object.keys(this._currentFilters).forEach(key => {
        const filterValue = (this._currentFilters as any)[key];
        const columnId = filterValue?.columnId || key;
        if (key === excludeColumnId || columnId === excludeColumnId) {
          return;
        }
        const filterField = filterValue?.queryKey || this.getFilterField(columnId);
        const processed = this.processFilterValue(filterValue);
        filters[filterField] = processed;
      });
    }
    return filters;
  }

  private getFilterField(columnId: string): string {
    if (this.grid) {
      const columns = this.grid.getColumns();
      const column = columns.find(
        (col: any) => col.id === columnId || col.field === columnId
      );
      if (column?.params?.queryFieldFilter) {
        return column.params.queryFieldFilter;
      }
    }
    return columnId;
  }

  private processFilterValue(filterValue: any): any {
    if (
      !filterValue ||
      typeof filterValue !== 'object' ||
      !('searchTerms' in filterValue)
    ) {
      return filterValue;
    }

    const searchTerms = filterValue.searchTerms || [];

    if (
      filterValue.operator === 'RangeInclusive' &&
      Array.isArray(searchTerms) &&
      searchTerms.length === 2
    ) {
      return this.buildRangeValue(searchTerms);
    }

    return this.extractFilterValue(filterValue, searchTerms);
  }

  private buildRangeValue(searchTerms: any[]): any {
    const [from, to] = searchTerms;
    const rangeValue: any = {};

    if (from !== undefined && from !== null) {
      rangeValue.from = from;
    }
    if (to !== undefined && to !== null) {
      rangeValue.to = to;
    }

    return rangeValue;
  }

  private extractFilterValue(filterValue: any, searchTerms: any[]): any {
    let extracted: any;

    if (searchTerms.length > 1) {
      extracted = searchTerms;
    } else if (searchTerms.length === 1) {
      extracted = searchTerms[0];
    } else {
      extracted = '';
    }

    const comparisonOperators = ['>', '<', '>=', '<=', '<>', '!', '!=', '='];
    const isTextFilter = filterValue.type === 'text';
    const hasComparisonOperator =
      filterValue.operator && comparisonOperators.includes(filterValue.operator);

    if (isTextFilter && hasComparisonOperator) {
      if (extracted === '' || extracted === null || extracted === undefined) {
        return filterValue.operator;
      }

      if (typeof extracted === 'string' && extracted !== '') {
          extracted = filterValue.operator + extracted;
      }
    }

    return extracted;
  }

  dispose(): void {
    this._currentFilters = [];
    this._currentSorters = [];
    this._currentPagination = null;
  }

  /**
   * Process backend response to update grid state
   * Called automatically by SlickGrid after backend response
   */
  postProcess(response: any): void {
    if (!response) return;

    if (this.pagination) {
      this.pagination.totalItems = response.totalCount || 0;
    }
  }
}
